import { Address, DateTimeISO, PaginatedResponse } from './common';
import { PublicCustomer } from './customers';
import { PublicStaff } from './staff';
import { PublicUser } from './user';

export type ExternalAccountsListQueryParams = {
  page?: number;
  statuses?: ExternalBankAccountStatus[];
  records_per_page?: number;
  consumer_ids?: string[];
};

export enum ExternalBankAccountStatus {
  PENDING = 0,
  VERIFIED = 1,
  AWAITING_E_SIGN = 2,
  SUBMITTED = 3,
  DECLINED = 4,
  AWAITING_CODE = 5,
  AWAITING_BANK_ADDRESS = 6,
  AWAITING_BANK_NAME = 7,
  DELETED = 8,
}

export type ExternalAccountListItem = {
  uuid: string;
  status: ExternalBankAccountStatus;
  accountName: string;
  consumer: PublicCustomer;
  lastUpdatedAt: DateTimeISO;
  bankCountryCode: string;
  bankLogoUrl: string;
};

export type ExternalAccountListAggregateInfo = {
  totalPending: number;
  totalVerified: number;
  totalAwaitingESign: number;
  totalSubmitted: number;
  totalDeclined: number;
  totalAwaitingCode: number;
  totalAwaitingBankAddress: number;
  totalAwaitingBankName: number;
  totalDeleted: number;
};

export type ExternalAccountsListResponse = {
  externalAccounts: PaginatedResponse<ExternalAccountListItem>;
  aggregateInfo: ExternalAccountListAggregateInfo;
};

export enum ExternalAccountCategory {
  CHECKING = 'checking',
  SAVINGS = 'savings',
  BROKERAGE = 'brokerage',
  CASH_MANAGEMENT = 'cash management',
  HSA = 'hsa',
  CD = 'cd',
  MONEY_MARKET = 'money market',
  PAYPAL = 'paypal',
  PREPAID = 'prepaid',
  EBT = 'ebt',
  CREDIT_CARD = 'credit card',
  AUTO = 'auto',
  BUSINESS = 'business',
  COMMERCIAL = 'commercial',
  CONSTRUCTION = 'construction',
  CONSUMER = 'consumer',
  HOME_EQUITY = 'home equity',
  LOAN = 'loan',
  MORTGAGE = 'mortgage',
  OVERDRAFT = 'overdraft',
  LINE_OF_CREDIT = 'line of credit',
  STUDENT = 'student',
  OTHER = 'other',
  FIVE_2_9 = '529',
  FOUR_0_1_A = '401a',
  FOUR_0_1_K = '401k',
  FOUR_0_3_B = '403B',
  FOUR_5_7_B = '457b',
  CASH_ISA = 'cash isa',
  CRYPTO_EXCHANGE = 'crypto exchange',
  EDUCATION_SAVINGS_ACCOUNT = 'education savings account',
  FIXED_ANNUITY = 'fixed annuity',
  GIC = 'gic',
  HEALTH_REIMBURSEMENT_ARRANGEMENT = 'health reimbursement arrangement',
  IRA = 'ira',
  ISA = 'isa',
  KEOGH = 'keogh',
  LIF = 'lif',
  LIFE_INSURANCE = 'life insurance',
  LIRA = 'lira',
  LRIF = 'lrif',
  LRSP = 'lrsp',
  MUTUAL_FUND = 'mutual fund',
  NON_CUSTODIAL_WALLET = 'non-custodial wallet',
  NON_TAXABLE_BROKERAGE_ACCOUNT = 'non-taxable brokerage account',
  OTHER_ANNUITY = 'other annuity',
  OTHER_INSURANCE = 'other insurance',
  PENSION = 'pension',
  PRIF = 'prif',
  PROFIT_SHARING_PLAN = 'profit sharing plan',
  QSHR = 'qshr',
  RDSP = 'rdsp',
  RESP = 'resp',
  RETIREMENT = 'retirement',
  RLIF = 'rlif',
  ROTH = 'roth',
  ROTH_401K = 'roth 401k',
  RRIF = 'rrif',
  RRSP = 'rrsp',
  SARSEP = 'sarsep',
  SEP_IRA = 'sep ira',
  SIMPLE_IRA = 'simple ira',
  SIPP = 'sipp',
  STOCK_PLAN = 'stock plan',
  THRIFT_SAVINGS_PLAN = 'thrift savings plan',
  TFSA = 'tfsa',
  TRUST = 'trust',
  UGMA = 'ugma',
  UTMA = 'utma',
  VARIABLE_ANNUITY = 'variable annuity',
}

export enum ExternalAccountVerifiedByType {
  STAFF_USER = 'Staff User',
  PLAID = 'Plaid',
}

export enum ExternalAccountVerificationType {
  ACCOUNT_PROOF = 'Account Proof',
  MICRODEPOSIT = 'Microdeposit',
  INSTANT_AUTH = 'Instant Auth',
}

export enum ExternalAccountSubmissionType {
  OFFLINE = 0,
  DIGITAL = 1,
}

export enum ExternalAccountConsentType {
  WET_SIGNATURE = 'Wet Signature',
  E_SIGN = 'E-Sign',
}

export enum ExternalAccountESignProvider {
  ONE_ADVISOR = '1Advisor',
  DOCUSIGN = 'Docusign',
}

export enum ExternalAccountType {
  CHECKING = 0,
  SAVINGS = 1,
  BROKERAGE = 2,
  OTHER = 3,
}

export type ExternalAccountDetails = {
  uuid: string;
  status: ExternalBankAccountStatus;
  category: ExternalAccountCategory;
  requestedAt: DateTimeISO;
  requestedBy?: PublicUser;
  submittedAt?: DateTimeISO;
  submittedBy?: PublicUser;
  verifiedByType: ExternalAccountVerifiedByType;
  verifiedBy?: PublicStaff;
  verifiedAt?: DateTimeISO;
  verificationType: ExternalAccountVerificationType;
  declinedBy?: PublicStaff;
  declinedAt?: DateTimeISO;
  declinedReason: string;
  submissionType: ExternalAccountSubmissionType | null;
  consentType: ExternalAccountConsentType;
  eSignProvider: ExternalAccountESignProvider;
  supportsWireTransfers: boolean;
  supportsAchTransfers: boolean;
  supportsTransactions: boolean;
  bankName?: string;
  accountType: ExternalAccountType;
  accountNumberLast4?: string;
  routingNumber?: string;
  bankAddress?: Address;
  owner: PublicCustomer;
  ownerAddress: Address;
  identityMatch?: {
    legalNameScore: number;
    phoneNumberScore: number;
    emailAddressScore: number;
    addressScore: number;
  };
  proofsOfOwnership: string[];
};

export type ExternalAccountFormValues = {
  companyId: string;
  customerId: string;
  bankName: string;
  accountType: string;
  paymentType: 'ach' | 'wire' | 'both' | '';
  accountNumber: string;
  confirmAccountNumber: string;
  routingNumber: string;
  bankAddress: Address;
  ownerName: string;
  ownerAddress: Address;
  bankAccountApplicationId: string;
  proofsOfOwnership: { value: string }[];
  documentDisplayNames: string[];
};

export type ExternalAccountActionButtonProps = {
  id: string;
  ownerId?: string;
};

export type DeclineExternalAccountForm = {
  id: string;
  reason: string;
};

export const queryKeys = {
  list: ['externalAccountsList'] as const,
  details: ['externalAccountsDetails'] as const,
};
